import { Entity } from "../../Entity";
import EventFactory from "../../../Entity/Factories/EventFactory";
import { EventInterface } from "./EventInterface";
import { FactoryInterface } from "../../../Entity/FactoryInterface";
import { UnitInterface } from "../Unit/UnitInterface";
import { ChargeInterface } from "../Charge/ChargeInterface";
import { PaymentInterface } from "../Payment/PaymentInterface";
import { ContractInterface } from "../Contract/ContractInterface";
import { PersonInterface } from "../Person/PersonInterface";
import { PropertyInterface } from "../Property/PropertyInterface";
import { CompanyInterface } from "../Company/CompanyInterface";
import { FinancialStatementInterface } from "../FinancialStatement/FinancialStatementInterface";
import Translator from "../../../Translation/Translator";
import Time from "../../../Time/Time";
import Formatter from "../../../Localization/Formatter";
import * as React from "react";
import * as moment from "moment";
import { AttachmentInterface } from "../Attachment/AttachmentInterface";

class Event extends Entity implements EventInterface {

  constructor(public data) {
    super();
  }

  get nameOfEntity(): string {
    return Translator.trans(this.entityTransKey);
  }

  get nameForUrl(): string {
    if (this.eventTypeId === 3) {
      return "notification";
    }

    if (this.eventTypeId === 17) {
      return "note";
    }

    if (this.eventTypeId === 34) {
      return "activity";
    }

    if (this.eventTypeId === 35 || this.eventTypeId === 38) {
      return "task";
    }

    return "event";
  }

  get entityTransKey(): string {
    if (this.eventTypeId === 3) {
      return "notification";
    }

    if (this.eventTypeId === 17) {
      return "note";
    }

    if (this.eventTypeId === 34) {
      return "activity";
    }

    if (this.eventTypeId === 35) {
      return "task";
    }

    if (this.eventTypeId === 38) {
      return "incident";
    }

    return "event";
  }

  public getFactory(): FactoryInterface {
    return EventFactory;
  }

  get id(): number {
    return this.data.id;
  }

  get eventTypeId(): number {
    return this.data.eventTypeId;
  }

  get unitId(): number {
    return this.data.unitId;
  }

  get unit(): UnitInterface {
    return this.newRelation("units.byId", this.data.unitId)[0];
  }

  get chargeId(): number {
    return this.data.chargeId;
  }

  get charge(): ChargeInterface {
    return this.data.charge;
  }

  get paymentId(): number {
    return this.data.paymentId;
  }

  get payment(): PaymentInterface {
    return this.data.payment;
  }

  get contractId(): number {
    return this.data.contractId;
  }

  get contract(): ContractInterface {
    return this.relation("app.contracts", this.data.contractId)[0];
  }

  get personId(): number {
    return this.data.personId;
  }

  get person(): PersonInterface {
    return this.relation("app.persons", this.data.personId)[0];
  }

  get companyId(): number {
    return this.data.companyId;
  }

  get company(): CompanyInterface {
    return this.relation("app.companies", this.data.companyId)[0];
  }

  get propertyId(): number {
    return this.data.propertyId;
  }

  get property(): PropertyInterface {
    return this.newRelation("properties.byId", this.data.propertyId)[0];
  }

  get event(): EventInterface {
    return this.data.event;
  }

  get createdBy(): number {
    return this.data.createdBy;
  }

  get updatedBy(): number {
    return this.data.updatedBy;
  }

  get creator(): PersonInterface {
    return this.relation("app.persons", this.data.createdBy)[0] || this.relation("app.persons", this.data.updatedBy)[0];
  }

  get creatorDisplayName(): string {
    return this.creator ? this.creator.displayName : Translator.trans("title-system");
  }

  get content(): any {
    return this.data.content;
  }

  get financialStatementId(): number {
    return this.data.financialStatementId;
  }

  get financialStatement(): FinancialStatementInterface {
    return this.relation("app.financialStatements", this.data.financialStatementId)[0];
  }

  get refersToEventId(): number {
    return this.data.refersToEventId;
  }

  get taskId(): number {
    return this.data.taskId;
  }
  get conversationId(): number {
    return this.data.conversationId;
  }

  get object() {
    if (!!this.payment) {
      return this.payment;
    }

    if (!!this.charge) {
      return this.charge;
    }

    if (!!this.event) {
      return this.event;
    }

    if (!!this.property) {
      return this.property;
    }

    if (!!this.unit) {
      return this.unit;
    }

    if (!!this.contract) {
      return this.contract;
    }

    if (!!this.company) {
      return this.company;
    }

    if (!!this.person) {
      return this.person;
    }
  }

  get hidden(): boolean {
    return this.data.hidden;
  }

  get removed(): boolean {
    return this.data.removed;
  }

  get createdAt(): Date {
    return this.data.createdAt;
  }

  get snoozeDate(): Date | null {
    return this.data.snoozeDate;
  }

  public shouldShowExpander(): boolean {
    return [11, 12, 13].indexOf(this.eventTypeId) === -1;
  }

  get read(): boolean {
    return this.data.read;
  }

  get viewed(): boolean {
    return this.data.viewed;
  }

  get reacted(): boolean {
    return this.data.reacted;
  }

  get name() {
    return this.data.content ? this.data.content.name ? this.data.content.name : "" : "";
  }

  get displayName(): string {
    return typeof this.content === "string" ?
      this.content :
      `${this.content && this.content.name ? this.content.name : ""} ${this.content && this.content.note ? this.content.note : ""} ${this.content && this.content.subject ? this.content.subject : ""} ${this.property ? this.property.displayName : this.unit ? this.unit.displayName : ""}`;
  }

  get listDisplayName(): string {
    return this.nameOfEntity + ", " + this.displayName;
  }

  get amazonFolderName(): string {
    return this.data.amazonFolderName;
  }

  get attachments(): AttachmentInterface[] {
    return this.data.attachments;
  }

  get reporterId(): number {
    return this.data.reporterId;
  }

  get reporter(): PersonInterface {
    return this.relation("app.persons", this.data.reporterId)[0];
  }
  get reporterCompany(): CompanyInterface {
    return this.reporter?.company || this.reporter?.companies[0];
  }

  get executorId(): number {
    return this.data.executorId || this.executor?.companies[0];
  }

  get executor(): PersonInterface {
    return this.relation("app.persons", this.data.executorId)[0];
  }
  get executorCompany(): CompanyInterface {
    return this.executor?.company || this.executor?.companies[0];
  }

  get executionDate(): Date | null {
    return this.data.executionDate;
  }
  
  get remindDate(): Date | null {
    return this.data.remindDate;
  }

  get status(): string {
    return this.reacted ? Translator.trans("status-closed") : Translator.trans("status-open");
  }

  get isTimelineEvent(): boolean {
    return (
      this.eventTypeId === 15 ||  // unpaid charge
      this.eventTypeId === 16 ||  // expiring contract
      this.eventTypeId === 33 ||  // adjust contract prices
      this.eventTypeId === 17 ||  // note
      this.eventTypeId === 34 ||  // activity
      this.eventTypeId === 35 ||  // task
      this.eventTypeId === 38 ||  // incident
      this.eventTypeId === 36     // email sent
    );
  }

  get countOfTasks(): number {
    return this.data.countOfTasks;
  }

  get countOfActiveTasks(): number {
    return this.data.countOfActiveTasks;
  }

  get countOfClosedTasks(): number {
    return this.data.countOfTasks - this.data.countOfActiveTasks;
  }

  get getIconColor(): string {
    if (this.eventTypeId === 15 || this.eventTypeId === 16 || this.eventTypeId === 33) {
      return "red";
    } else if (this.eventTypeId === 35 || this.eventTypeId === 38) {
      if (!!this.executionDate &&
        this.executionDate instanceof Date &&
        // comparing without time 
        new Date(this.executionDate).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0)) {
        return "red";
      } else {
        return "blue";
      }
    } else {
      return "#00BCD4";
    }
  }

  get subject(): string {
    switch (this.data.eventTypeId) {
      case 1:
      case 2:
        return this.data.content.letter.subject;

      case 3:
        return Translator.trans("reminder");

      case 11:
        return (
          Translator.trans("event-system-created-title", {
            entity: Translator.trans(`title-${Object.keys(this.data.content || {})[0]}`)
          })
        );

      case 12:
        return (
          Translator.trans("event-system-updated-title", {
            entity: Translator.trans(`title-${Object.keys(this.data.content || {})[0]}`)
          })
        );

      case 13:
        return (
          Translator.trans("event-system-removed-title", {
            entity: Translator.trans(`title-${Object.keys(this.data.content || {})[0]}`)
          })
        );

      case 15:
        return `${Translator.trans("unpaid-charge")}: ${this.charge && this.charge.description}`

      case 16:
        return Translator.trans(`expiring-contract`);

      case 17:
        return this.data.content.note;

      case 18:
        return Translator.trans(`event-contract-ended-title`);

      case 19:
        return Translator.trans("created-owner-statement");

      case 20:
        return Translator.trans("owner-statement-warning");

      case 21:
        return Translator.trans(`event-contract-ending-title`);

      case 22:
        return Translator.trans("sent-owner-statement");

      case 23:
      case 26:
        return Translator.trans("event-charge-linked");

      case 24:
      case 27:
        return Translator.trans("event-charge-unlinked");

      case 25:
        return (
          Translator.trans("task-was-finished", { task: this.data.content.task.name })
        );

      case 28:
        return (
          Translator.trans("task-was-created", { task: this.data.content.task.name })
        );

      case 29:
        return (
          Translator.trans("task-was-updated", { task: this.data.content.task.name })
        );

      case 30:
        return (
          Translator.trans("task-was-removed", { task: this.data.content.task.name })
        );

      case 32:
        return (
          Translator.trans("charge-was-sent")
        );

      case 33:
        return Translator.trans("event-update-contract-prices");

      case 34:
        return (
          `${Translator.trans("activity")} ${this.content && this.content.name}`
        );

      case 35:
        return (
          `${Translator.trans("task")} ${this.content && this.content.name}`
        );

      case 36:
        return (
          `${Translator.trans("email-was-sent")}`
        );

      case 37:
        return (
          `${Translator.trans("account-confirmed")}`
        );

      case 38:
        return (
          `${Translator.trans("incident")}`
        );

      case 39:
        return (
          `${Translator.trans("event-charge-settled")}`
        );

      case 40:
        return (
          `${Translator.trans("event-charge-unsettled")}`
        );

      case 41:
        return (
          `${Translator.trans("event-charge-settled-savings")}`
        );

      case 42:
        return (
          `${Translator.trans("event-charge-unsettled-savings")}`
        );

      case 43:
        return (
          `${Translator.trans("event-payment-refunded")}`
        );

      case 44:
        return (
          `${Translator.trans("event-payment-unrefunded")}`
        );

      case 45:
        return (
          `${Translator.trans("event-charge-credited")}`
        );

      case 46:
        return (
          `${Translator.trans("event-charge-uncredited")}`
        );

      case 47:
        return (
          `${Translator.trans("event-ponto-synchronization")}`
        );

      case 48:
        return (
          `${Translator.trans("event-statement-was-created")}`
        );

      case 49:
        return (
          `${Translator.trans("event-statement-was-closed")}`
        );

      default:
        return `Unsupported event type: ${this.data.eventTypeId}`;
    }
  }

  get reason(): string | JSX.Element {
    switch (this.data.eventTypeId) {
      case 1:
      case 2:
        return this.data.content.letter.text;

      case 3:
      case 11:
      case 12:
      case 13:
        return "";

      case 15:
        return Translator.transJSX(`event-charge-overdue-reason`, {
          charge: (
            <span
              className="link-to-entity"
              onClick={() => this.redirect(`/app/charge/update/${this.charge && this.charge.id}`)}
              key={`event-reason-charge-${this.charge && this.charge.id}`}>{this.charge && this.charge.description}
            </span>),
          date: Formatter.formatDate(this.charge && this.charge.firstDate)
        });

      case 16:
        return Translator.transJSX(`event-contract-expiring-reason`, {
          tenant: (
            <span className="link-to-entity" onClick={() => {
              if (this.contract?.mainTenantCompanyId) {
                this.redirect(`/app/company/update/${this.contract?.mainTenantCompanyId}`);
              } else {
                this.redirect(`/app/person/update/${this.contract?.mainTenantContactId}`);
              }
            }}
              key={`event-reason-expiring-contract-tenant-${this.id}`}>{this.contract?.tenantDisplayName}
            </span>),
          unit: (
            <span className="link-to-entity" onClick={() => this.redirect(`/app/unit/update/${this.contract && this.contract.unit && this.contract.unit.id}`)}
              key={`event-reason-expiring-contract-unit-${this.contract && this.contract.unit && this.contract.unit.id}`}>{this.contract && this.contract.unit && this.contract.unit.displayName}
            </span>),
          date: Formatter.formatDate(this.contract && this.contract.expirationDate) ||
            Formatter.formatDate(this.contract && this.contract.finalRentalPeriodDate)
        });

      case 17:
        return this.data.content.note;

      case 18:
        return Translator.trans(`event-contract-ended-reason`, {
          date: Formatter.formatDate(this.contract.endDate)
        });

      case 19:
        return Translator.transJSX("owner-statement-was-created",
          {
            month: this.financialStatement ? this.financialStatement.period.toLocaleDateString() : ""
          });

      case 20:
        const monthTitle = Time.getMonthDetail(moment(this.createdAt).subtract(1, "months").format("YYYY-MM-DD"), "title");

        return Translator.transJSX(`owner-statement-wasnt-sent`, {
          month: monthTitle,
          sendButton: (
            <span
              className="link-to-entity"
              onClick={() => this.redirect(`/app/financial-statement/create/${this.companyId}/${Time.getMonthDetail(moment(this.createdAt).subtract(1, "months").format("YYYY-MM-DD"), "index")}`)}
              key={`event-reason-statemnt-was-not-sent-${Time.getMonthDetail(moment(this.createdAt).subtract(1, "months").format("YYYY-MM-DD"), "index")}-${this.data.companyId}`}
            >
              {Translator.trans("send-owner-statement-for", {
                month: monthTitle
              })}
            </span>)
        });

      case 21:
        return Translator.transJSX(`event-contract-ending-reason`, {
          tenant: (
            <span className="link-to-entity" onClick={() => {
              if (this.contract?.mainTenantCompanyId) {
                this.redirect(`/app/company/update/${this.contract?.mainTenantCompanyId}`);
              } else {
                this.redirect(`/app/person/update/${this.contract?.mainTenantContactId}`);
              }
            }}
              key={`event-reason-ending-contract-tenant-${this.id}`}>{this.contract?.tenantDisplayName}
            </span>),
          unit: (
            <span className="link-to-entity" onClick={() => this.redirect(`/app/unit/update/${this.contract && this.contract.unit && this.contract.unit.id}`)}
              key={`event-reason-ending-contract-unit-${this.contract && this.contract.unit && this.contract.unit.id}`}>{this.contract && this.contract.unit && this.contract.unit.displayName}
            </span>),
          date: Formatter.formatDate(this.contract && this.contract.endDate)
        });

      case 22:
        return Translator.transJSX("owner-statement-was-sent",
          {
            month: this.financialStatement.period.toLocaleDateString()
          });

      case 23:
        return Translator.transJSX("charge-paid-with-payment", {
          charge: (
            <span
              className="link-to-entity"
              onClick={() => this.redirect(`/app/charge/update/${this.charge && this.charge.id}`)}
              key={`event-reason-charge-payment-linked-charge-${this.chargeId}`}>
              {this.charge && this.charge.description}
            </span>),
          payment: (
            <span className="link-to-entity" onClick={() => this.redirect(`/app/payment/update/${this.paymentId}`)} key={`event-reason-charge-payment-linked-payment-${this.paymentId}`}>
              {this.payment && this.payment.description}
            </span>),
        });

      case 24:
        return Translator.transJSX("charge-and-payment-were-unlinked", {
          charge: (
            <span
              className="link-to-entity"
              onClick={() => this.redirect(`/app/charge/update/${this.charge && this.charge.id}`)}
              key={`event-reason-charge-payment-unlinked-charge-${this.chargeId}`}>
              {this.charge && this.charge.description}
            </span>),
          payment: (
            <span className="link-to-entity" onClick={() => this.redirect(`/app/payment/update/${this.paymentId}`)} key={`event-reason-charge-payment-unlinked-payment-${this.paymentId}`}>
              {this.payment && this.payment.description}
            </span>),
        });

      case 25:
        return (
          <div >
            {
              this.unit && Translator.transJSX("task-for-unit-done", {
                unit: (
                  <span className="link-to-entity" onClick={() => this.redirect(`/app/unit/update/${this.unitId}`)} key={`event-reason-task-for-unit-done-unit-${this.unitId}`}>
                    {this.unit && this.unit.displayName ? this.unit.displayName : ""}
                  </span>
                )
              })
            }
            {
              this.property && Translator.transJSX("task-for-property-done", {
                property: (
                  <span className="link-to-entity" onClick={() => this.redirect(`/app/property/update/${this.propertyId}`)} key={`event-reason-task-for-property-done-property-${this.propertyId}`} >
                    {this.property && this.property.displayName ? this.property.displayName : ""}
                  </span>
                )
              })
            }
            {this.person &&
              Translator.transJSX("done-by-executor", {
                executor: (
                  <span className="link-to-entity" onClick={() => this.redirect(`/app/person/update/${this.personId}`)} key={`event-reason-task-done-by-executor-${this.personId}`} >
                    {this.person && this.person.displayName ? this.person.displayName : ""}
                  </span>
                )
              })
            }
          </div>
        );

      case 26:
        return Translator.transJSX("company-and-person-were-linked", {
          company: (
            <span
              className="link-to-entity"
              onClick={() => this.company?.isOwner ?
                this.redirect.bind(this, `/app/owner/update/${this.company.id}`) :
                this.company?.isAgency ?
                  this.redirect.bind(this, `/app/agency/update/${this.company.id}`) :
                  this.redirect.bind(this, `/app/company/update/${this.company.id}`)
              }
              key={`event-company-person-were-linked-company-${this.companyId}`}
            >
              {this.company && this.company.displayName ? this.company.displayName : ""}
            </span>),
          person: (
            <span className="link-to-entity" onClick={() => this.redirect(`/app/person/update/${this.personId}`)} key={`event-company-person-were-linked-person-${this.personId}`}>
              {this.person && this.person.displayName ? this.person.displayName : ""}
            </span>),
        });

      case 27:
        return Translator.transJSX("charge-and-payment-were-unlinked", {
          charge: (
            <span
              className="link-to-entity"
              onClick={() => this.redirect(`/app/charge/update/${this.charge && this.charge.id}`)}
              key={`event-reason-charge-payment-unlinked-charge-${this.chargeId}`}>
              {this.charge && this.charge.description}
            </span>),
          payment: (
            <span className="link-to-entity" onClick={() => this.redirect(`/app/payment/update/${this.paymentId}`)} key={`event-reason-charge-payment-unlinked-payment-${this.paymentId}`}>
              {this.payment && this.payment.description}
            </span>),
        });

      case 28:
        return (
          <div >
            {
              this.unit && Translator.transJSX("task-for-unit-created", {
                unit: (
                  <span className="link-to-entity" onClick={() => this.redirect(`/app/unit/update/${this.unitId}`)} key={`event-reason-task-for-unit-created-unit-${this.unitId}`}>
                    {this.unit && this.unit.displayName ? this.unit.displayName : ""}
                  </span>
                )
              })
            }
            {
              this.property && Translator.transJSX("task-for-property-created", {
                property: (
                  <span className="link-to-entity" onClick={() => this.redirect(`/app/property/update/${this.propertyId}`)} key={`event-reason-task-for-property-created-property-${this.propertyId}`} >
                    {this.property && this.property.displayName ? this.property.displayName : ""}
                  </span>
                )
              })
            }
          </div>
        );

      case 29:
        return (
          <div >
            {
              this.unit && Translator.transJSX("task-for-unit-updated", {
                unit: (
                  <span className="link-to-entity" onClick={() => this.redirect(`/app/unit/update/${this.unitId}`)} key={`event-reason-task-for-unit-updated-unit-${this.unitId}`}>
                    {this.unit && this.unit.displayName ? this.unit.displayName : ""}
                  </span>
                )
              })
            }
            {
              this.property && Translator.transJSX("task-for-property-updated", {
                property: (
                  <span className="link-to-entity" onClick={() => this.redirect(`/app/property/update/${this.propertyId}`)} key={`event-reason-task-for-property-updated-property-${this.propertyId}`} >
                    {this.property && this.property.displayName ? this.property.displayName : ""}
                  </span>
                )
              })
            }
          </div>
        );

      case 30:
        return (
          <div >
            {
              this.unit && Translator.transJSX("task-for-unit-removed", {
                unit: (
                  <span className="link-to-entity" onClick={() => this.redirect(`/app/unit/update/${this.unitId}`)} key={`event-reason-task-for-unit-removed-unit-${this.unitId}`}>
                    {this.unit && this.unit.displayName ? this.unit.displayName : ""}
                  </span>
                )
              })
            }
            {
              this.property && Translator.transJSX("task-for-property-removed", {
                property: (
                  <span className="link-to-entity" onClick={() => this.redirect(`/app/property/update/${this.propertyId}`)} key={`event-reason-task-for-property-removed-property-${this.propertyId}`} >
                    {this.property && this.property.displayName ? this.property.displayName : ""}
                  </span>
                )
              })
            }
          </div>
        );

      case 32:
      case 33:
      case 36:
      case 37:
      case 39:
      case 40:
      case 41:
      case 42:
      case 43:
      case 44:
      case 45:
      case 46:
      case 48:
      case 49:
        return "";

      case 34:
        return (
          <div>
            <div>{this.content && this.content.subject}</div>
            <div>{this.content && this.content.description}</div>
          </div>
        );

      case 35:
        return (
          <div>
            <div>{this.content && this.content.name}</div>
            <div>{this.content && this.content.details}</div>
          </div>
        );

      case 47:
        return (
          `${Translator.trans("event-ponto-synchronization")}`
        );

      default:
        return `Unsupported event type: ${this.data.eventTypeId}`;
    }
  }

  get image(): any {
    switch (this.data.eventTypeId) {
      case 1:
      case 2:
      case 22:
      case 36:
        return (
          <div className="icon-wrapper">
            <div className="icon icon-ic-email"></div>
          </div>
        );

      case 3:
        return (
          <div className="icon-wrapper">
            <div className="icon icon-ic-notification"></div>
          </div>
        );

      case 11:
      case 19:
        return (
          <div className="icon-wrapper">
            <div className="icon icon-ic-created"></div>
          </div>
        );

      case 12:
        return (
          <div className="icon-wrapper">
            <div className="icon icon-ic-edited"></div>
          </div>
        );

      case 13:
      case 18:
        return (
          <div className="icon-wrapper">
            <div className="icon icon-ic-remove icon-color remove-icon"></div>
          </div>
        );

      case 15:
      case 16:
      case 33:
        return (
          <div className="icon-wrapper">
            <div className={!this.reacted ? `icon icon-ic-info ${this.getIconColor}` : "icon icon-ic-info"}></div>
          </div>
        );

      case 17:
        return (
          <div className="icon-wrapper">
            <div className="icon icon-ic-message"></div>
          </div>
        );

      case 23:
      case 26:
        return (
          <div className="icon icon-ic-link" style={{ color: "#b2b2b2" }}></div>
        );

      case 24:
      case 27:
        return (
          <div className="icon icon-ic-call-split" style={{ color: "#b2b2b2" }}></div>
        );

      case 25:
      case 28:
      case 29:
      case 30:
        return (
          <div className="icon-wrapper">
            <div className="icon icon-ic-task"></div>
          </div>
        );

      case 34:
        return (
          <div className="icon-wrapper">
            <div className={!this.reacted ? `icon icon-ic-activity ${this.getIconColor}` : "icon icon-ic-activity"}></div>
          </div>
        );

      case 35:
        return (
          <div className="icon-wrapper">
            <div className={!this.reacted ? `icon icon-ic-task ${this.getIconColor}` : "icon icon-ic-task"}></div>
          </div>
        );


      default:
        return (
          <div className="icon-wrapper">
            <div className="icon icon-ic-info"></div>
          </div>
        );
    }
  }
}

export { Event }
